import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { fontWeight, radius, size } from '@platform/tokens';
import { useCallback } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { ActionFeed } from '../actions/ActionFeed';
import { ActionsProvider } from '../actions/ActionsProvider';
import { useAuth } from '../auth/AuthProvider';
import { UnsavedProvider } from '../authoring/unsaved';
import { AskAiProvider } from '../curriculum/AskAiProvider';

import { Crumbs } from './Crumbs';
import { SECTIONS, sectionFor } from './nav';
import { SCREEN_GAP } from './PageHeader';

/**
 * The layout every signed-in screen renders inside — `004` §3, and what the old
 * placeholder shell said `ADMIN-03` would replace it with.
 *
 * A layout route rather than a component each screen imports. Nesting puts `RequireAuth`
 * in one place instead of once per route, which is what the route table had: six copies,
 * and a seventh route was one omission away from rendering unauthenticated.
 *
 * `permanent` because this is a desktop tool. §11.1 settled that, and a drawer that
 * collapses to a hamburger costs a click on every navigation to save width nobody is
 * short of.
 */
const DRAWER_WIDTH = '14rem';

const NAV = { px: 1 } as const;
const NAV_ROW = {
  minHeight: size.lg,
  py: 0,
  // A string, because `sx` multiplies a bare number by `shape.borderRadius`.
  borderRadius: `${String(radius.md)}px`,
  '&.Mui-selected .MuiListItemText-primary': {
    fontWeight: Number(fontWeight.semibold),
  },
} as const;

export function AppShell() {
  const auth = useAuth();
  const { pathname } = useLocation();

  // Resolved once for the whole list. `undefined` is the root, which owns nothing below
  // it — see `sectionFor`.
  const here = sectionFor(pathname);

  const signOut = useCallback(() => {
    void auth.signOut();
  }, [auth]);

  // Both providers wrap the whole shell rather than each screen: a mutation is reported by
  // existing, not by a screen remembering to opt in — GEN-41. `UnsavedProvider` is here for
  // the same reason and the same producer — every `EditableText` reports its own unsaved
  // text, so `CourseDetail` gets the leave-the-page guard without opting in, and only the
  // screen that renders `SaveState` shows a count — GEN-55.
  return (
    <ActionsProvider>
      <AskAiProvider>
        <UnsavedProvider>
          <Box sx={{ display: 'flex', height: '100dvh' }}>
            <Drawer
              variant="permanent"
              sx={{
                width: DRAWER_WIDTH,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: DRAWER_WIDTH,
                  boxSizing: 'border-box',
                },
              }}
            >
              <Toolbar>
                <Typography variant="h6" noWrap>
                  Admin
                </Typography>
              </Toolbar>
              <Divider />

              <List sx={NAV}>
                {SECTIONS.map((section) => (
                  <ListItemButton
                    key={section.path}
                    sx={NAV_ROW}
                    component={Link}
                    to={section.path}
                    // Shared with the breadcrumb rather than a second prefix test: they answer
                    // the same question, and two answers can disagree.
                    selected={
                      section.path === '/'
                        ? here === undefined
                        : here?.path === section.path
                    }
                  >
                    <ListItemText primary={section.label} />
                  </ListItemButton>
                ))}
              </List>
            </Drawer>

            <Box
              component="main"
              sx={{
                flex: 1,
                p: SCREEN_GAP,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: SCREEN_GAP,
                }}
              >
                <Crumbs />
                <Button size="small" onClick={signOut}>
                  Sign out
                </Button>
              </Stack>

              {/* The feed takes its space out of the shell, not out of the screen — GEN-69.
                Put inside the scroll region instead, it sits above a screen already
                claiming the region's full height, so one refusal alert pushes that
                screen's last 124px past the bottom edge: the review workspace's Ask
                button became unclickable. Bounded and scrolling, because a run of
                refusals must not squeeze the screen out either. */}
              <Box sx={{ flexShrink: 0, maxHeight: '40%', overflowY: 'auto' }}>
                <ActionFeed />
              </Box>

              {/* The screen scrolls here, not the document. A screen that wants to pin part
                of itself, as the review workspace pins the preview and the chat, needs a
                parent whose height is known; against an auto-height shell its
                `height: 100%` resolved to auto and the whole page scrolled instead. */}
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Outlet />
              </Box>
            </Box>
          </Box>
        </UnsavedProvider>
      </AskAiProvider>
    </ActionsProvider>
  );
}
